import {
  faqs,
  howItWorks,
  purchaseSeatsLanguages,
  MWCBBallLink,
} from "./helpers";
import { routes } from "@/global/routes";
import SeatMap3dPreview from "public/images/diamondbacks/seat_map_preview.png";
import BackgroundImage from "public/images/mwc/mwc_background_google.webp";
import BannerImage from "public/images/mwc/AttendBanner.png";
import { TeamConfig } from "../types";
import { vendorUUIDs } from "../../constants";
import {
  DefaultRegistrationTerms,
  DefaultCheckoutTerms,
} from "../../default_terms_and_conditions";

const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/NCAA/Mountain_West_Conference.png";

/*
 +------------------------------------------------------------------------------+
 * // TODO 
 * 1. Assets (using google assets for now )
 * 2. Seat Map Id
 * 3. Inventory Type
 * 4. Account Manager URL
 * 5. Enable Available Credit ? 
 * 6. Faqs and How It works languages
 * 7. Terms and Conditions
 * 8. Enable Intercom ? 
 * 9. Enable Purchase Credit ?
 * 10. Enable Gift Card ?
 * 11. Enable Zip Restriction ?
 +------------------------------------------------------------------------------+ */

const config: TeamConfig = {
  teamSchema: "mwc_bball",
  browserTitle: "Mountain West Conference",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Mountain West Conference",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-10460-basketball-sec",
    mapId: "blockmap2d",
  },

  packs: {
    enabled: true,
    dontOpenPackModal: false,
    list: [
      {
        id: 1,
        amount: 3,
        title: "3 Session Pack",
        description: "Select 3 sessions for 15% off your total ticket price!",
        offerMoreMessage:
          "Would you like to add 2 more sessions and get 20% off your tickets?",
        defaultSelected: true,
        buckets: [
          {
            id: 1,
            title: "Pick 3 Sessions",
            description: "",
            max: 3,
            min: 3,
            dontCloseWhenHitMin: true,
          },
        ],
        updatedAccountGroup: 0,
        successMessage:
          "Congratulations! you have unlocked 15% off your tickets.",
      },
      {
        id: 2,
        amount: 5,
        title: "5 Session Pack",
        description: "Select 5 sessions for 20% off your total ticket price!",
        offerMoreMessage: "",
        buckets: [
          {
            id: 1,
            title: "Pick 5 Sessions",
            description: "",
            max: 5,
            min: 5,
            dontCloseWhenHitMin: true,
          },
        ],
        updatedAccountGroup: 1,
        successMessage:
          "Congratulations! you have unlocked 20% off your tickets.",
      },
    ],
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: false,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 3,
    minSeatLimit: 1,
    maxGameSelectionCount: 5,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,

    defaultSeatCount: 2,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  isSaveCardAllowed: true,

  oktaClientId: "",
  oktaIssuer: "",
  platform: vendorUUIDs.ticketmaster,
  refreshInventoryWhenEventClick: false,

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/mountainwest/",

  // images
  images: {
    findGamesBannerImage: BannerImage.src,
    authBackgroundImage: BackgroundImage.src,
    checkoutStatusPageImage: BackgroundImage.src,
    giftCardStandalonePageImage: BannerImage.src,
    logoImage: LogoImage + "?width=76&height=76",
    logoNameImage: "",
    faviconImage: LogoImage,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },
  },

  colors: {
    primary: "#4f2d7f",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: purchaseSeatsLanguages,
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessDescription: (
      <>
        Your Mountain West Conference tickets are confirmed! Tickets will be
        available in your Mountain West <MWCBBallLink />.
      </>
    ),

    giftCard: {
      title: "Give The Gift Of Broadway",
      createGiftCard: {
        description:
          "Share the magic of Broadway with a friend or loved one. Upon purchase of a gift card, a unique code will be generated for the intended recipient to redeem. Once redeemed, the gift card is not refundable.",
        recipient: {
          sendNowFalse: {
            description:
              "If an intended recipient is not specified, once purchased, you will have to forward the unique code to the recipient. Upon your recipient receiving their gift card, they will be prompted to enter their unique code to redeem the gift card. Once redeemed, the recipient will receive an account credit that they can use to purchase tickets for all Hippodrome Broadway Series shows in Baltimore.",
          },
          sendNowTrue: {
            description:
              "The Recipient will be sent an email with the unique code for them to redeem their gift card. Once redeemed, the recipient will receive an account credit that they can use to purchase tickets for all Hippodrome Broadway Series shows in Baltimore.",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description:
            "Please input the case sensitive code provided to redeem the value of the gift card to the Hippodrome Broadway Series in Baltimore account above.",
        },
        logout: {
          description:
            "Please input the case sensitive code provided to redeem the value of the gift card to the Hippodrome Broadway Series in Baltimore.",
        },
        success: {
          description:
            "has been added to your Hippodrome Broadway Series in Baltimore Account Manager account!",
        },
      },
    },
    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
